"use client";

import { ChevronDown, MapPin, Package, X } from "@/components/icons";
import { Image } from "@/components/ui/image";
import { SimpleZipInput } from "@/components/inputs/simple-zip-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useListingBrowseContext } from "../context/listing-browse-context";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import {
  PRODUCT_TYPES,
  PRODUCT_TYPE_CONDITIONS,
  getProductTypeBySlug,
  isProductType,
  type ProductType,
} from "@/lib/generated/product-types";
import type { GeocodeResult } from "@/lib/geocoding/geocode";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { SegmentedControl } from "@/components/ui/segmented-control";

export const QUANTITY_PRESETS = [1, 24, 48, 96, 200] as const;
export const PRODUCT_TYPE_OPTIONS = [...PRODUCT_TYPES].sort(
  (left, right) => left.displayOrder - right.displayOrder,
);

function FilterTrigger({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <Button
      type="button"
      variant="outline"
      size="compact"
      className={cn("rounded-sm", className)}
      {...props}
    >
      {children}
    </Button>
  );
}

function FilterOption({
  isSelected,
  onSelect,
  children,
}: {
  isSelected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="compact"
      onClick={onSelect}
      className={cn(
        "text-left w-full justify-start rounded-sm text-sm",
        isSelected && "hover:bg-action-tint bg-action-tint text-action",
      )}
    >
      {children}
    </Button>
  );
}

export function conditionsKeptFor(
  productTypeSlug: string,
  conditions: readonly string[],
): string[] {
  const offered = isProductType(productTypeSlug)
    ? (PRODUCT_TYPE_CONDITIONS[productTypeSlug] ?? [])
    : [];
  const offeredValues = new Set(offered.map((option) => option.value));
  return conditions.filter((entry) => offeredValues.has(entry));
}

export function useSelectProductType() {
  const { setProductType, conditions, setConditions } = useListingBrowseContext();
  return (productTypeSlug: string) => {
    setProductType(productTypeSlug);
    const kept = conditionsKeptFor(productTypeSlug, conditions);
    if (kept.length !== conditions.length) setConditions(kept);
  };
}

function ProductTypeTrigger({
  iconUrl,
  label,
}: {
  iconUrl: string | null | undefined;
  label: string;
}) {
  return (
    <PopoverTrigger asChild>
      <FilterTrigger>
        {iconUrl ? (
          <Image src={iconUrl} alt="" width={16} height={16} className="size-4 object-contain" />
        ) : (
          <Package className="text-muted-foreground" />
        )}
        <span className="text-foreground">{label}</span>
        <ChevronDown className="text-muted-foreground" />
      </FilterTrigger>
    </PopoverTrigger>
  );
}

export function ProductTypeFilter() {
  const { productType, setProductType, lockedProductType } = useListingBrowseContext();
  const selectProductType = useSelectProductType();
  if (lockedProductType) return null;

  const selectedType = productType ? getProductTypeBySlug(productType) : null;
  const displayLabel = selectedType?.pluralDisplayName ?? "All Products";

  return (
    <Popover>
      <ProductTypeTrigger iconUrl={selectedType?.iconUrl} label={displayLabel} />
      <PopoverContent
        align="start"
        className="w-[220px] p-1 rounded-sm border-border"
      >
        <ScrollArea className="max-h-[320px]">
          <FilterOption
            isSelected={productType === null}
            onSelect={() => setProductType(null)}
          >
            <Package className="text-muted-foreground" />
            <span>All Products</span>
          </FilterOption>
          {PRODUCT_TYPE_OPTIONS.map((type) => (
            <ProductTypeOption
              key={type.slug}
              type={type}
              isSelected={productType === type.slug}
              onSelect={() => selectProductType(type.slug)}
            />
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function ProductTypeOption({
  type,
  isSelected,
  onSelect,
}: {
  type: ProductType;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <FilterOption isSelected={isSelected} onSelect={onSelect}>
      {type.iconUrl ? (
        <Image
          src={type.iconUrl}
          alt=""
          width={16}
          height={16}
          className="size-4 object-contain"
        />
      ) : (
        <Package className="text-muted-foreground" />
      )}
      <span>{type.pluralDisplayName}</span>
    </FilterOption>
  );
}

export const RADIUS_PRESETS = [
  { value: 25, label: "25 mi" },
  { value: 50, label: "50 mi" },
  { value: 100, label: "100 mi" },
  { value: 200, label: "200 mi" },
  { value: 500, label: "500 mi" },
  { value: 0, label: "Nationwide" },
] as const;

export function useLocationHandlers() {
  const heroMap = useHeroMapContext();
  const { setStateCode } = useListingBrowseContext();

  const handleZipSelect = (data: GeocodeResult, postalCode: string) => {
    setStateCode(null);
    heroMap.setLocation({
      lat: data.latitude,
      lng: data.longitude,
      postalCode,
      city: data.city,
      state: data.state,
    });
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setStateCode(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        heroMap.setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          postalCode: undefined,
          city: undefined,
          state: undefined,
        });
      },
      () => {},
    );
  };

  return {
    handleZipSelect,
    handleUseMyLocation,
    handleClear: () => heroMap.setLocation(null),
  };
}

export function LocationFilter() {
  const heroMap = useHeroMapContext();
  const location = heroMap.location;
  const radiusMiles = heroMap.radiusMiles ?? 50;
  const { handleZipSelect, handleUseMyLocation, handleClear } =
    useLocationHandlers();

  const displayLocation = location?.postalCode
    ? location.city
      ? `${location.city}, ${location.state}`
      : location.postalCode
    : location?.lat
      ? "My Location"
      : null;

  const radiusLabel = radiusMiles === 0 ? "Nationwide" : `${radiusMiles} mi`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterTrigger className="min-w-[180px]">
          <MapPin className="text-muted-foreground" />
          <span className="text-foreground truncate">
            {displayLocation ?? "Location"}
          </span>
          {displayLocation && (
            <span className="text-muted-foreground text-xs">
              ({radiusLabel})
            </span>
          )}
          <ChevronDown className="ml-auto text-muted-foreground" />
        </FilterTrigger>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[280px] p-3 rounded-sm border-border"
      >
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1.5">ZIP Code</div>
            <div className="relative">
              <SimpleZipInput
                value={location?.postalCode ?? ""}
                onChange={() => {}}
                onZipSelect={handleZipSelect}
                placeholder="Enter ZIP code"
                className={cn(
                  "h-9 w-full px-3",
                  "text-sm border border-border rounded-sm bg-card",
                  "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]",
                )}
                showValidIndicator={false}
              />
              {displayLocation && (
                <IconButton
                  variant="quiet"
                  size="inline"
                  onClick={handleClear}
                  aria-label="Clear location"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <X />
                </IconButton>
              )}
            </div>
            {displayLocation && displayLocation !== "My Location" && (
              <div className="text-xs text-muted-foreground mt-1">
                {displayLocation}
              </div>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            size="compact"
            onClick={handleUseMyLocation}
            className="w-full rounded-sm"
          >
            <MapPin />
            Use my location
          </Button>

          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1.5">Search Radius</div>
            <SegmentedControl
              size="compact"
              aria-label="Search radius"
              value={String(radiusMiles)}
              onValueChange={(next) => heroMap.setRadiusMiles(Number(next))}
              options={RADIUS_PRESETS.map((preset) => ({
                value: String(preset.value),
                label: preset.label,
              }))}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function QuantityFilter() {
  const heroMap = useHeroMapContext();
  const quantity = heroMap.quantity ?? 1;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterTrigger>
          <span className="text-muted-foreground">Qty:</span>
          <span className="tabular-nums text-foreground">{quantity}</span>
          <ChevronDown className="text-muted-foreground" />
        </FilterTrigger>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[180px] p-2 rounded-sm border-border"
      >
        <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">Quantity</div>
        <SegmentedControl
          size="compact"
          aria-label="Quantity"
          className="mb-2 tabular-nums"
          value={String(quantity)}
          onValueChange={(next) => heroMap.setQuantity(Number(next))}
          options={QUANTITY_PRESETS.map((preset) => ({
            value: String(preset),
            label: String(preset),
          }))}
        />
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={(e) =>
            heroMap.setQuantity(Math.max(1, parseInt(e.target.value) || 1))
          }
          className="w-full h-8 px-2 text-sm tabular-nums border border-border rounded-sm bg-card text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]"
          placeholder="Custom qty"
        />
      </PopoverContent>
    </Popover>
  );
}
